import Phaser from 'phaser';
import { EnemyWeaponType, TileType, TILE_SIZE } from '@/config/GameConfig';
import { MAP_GRID } from '@/map/MapData';
import { SpatialHash } from '@/systems/SpatialHash';
import { GOD } from '@/config/GodMode';

type RobotTarget = {
  x: number; y: number; alive: boolean;
  takeDamage: (d: number, ax?: number, ay?: number) => void;
  collisionRadius?: number;
  checkRicochet?: (weaponType: EnemyWeaponType, ax: number, ay: number) =>
    { normalX: number; normalY: number; impactX: number; impactY: number } | null;
};

type EnemyTarget = {
  x: number; y: number; alive: boolean;
  takeDamage: (d: number) => void;
  collisionRadius?: number;
};

// ── Trail constants ──
const TRAIL_MAX = 60;         // max trail points (luminous)
const SMOKE_INTERVAL = 10;    // px between smoke puffs (missiles)

// ── Projectile visual: Container with circle children (reliable WebGL rendering) ──
interface EnemyProjectile {
  visual: Phaser.GameObjects.Container;
  x: number; y: number;
  vx: number; vy: number;
  damage: number; color: number;
  alive: boolean;
  maxDist: number;
  traveled: number;
  weaponType: EnemyWeaponType;
  // Missile homing
  tx?: number; ty?: number;
  homingStrength?: number;
  speed: number;
  // Trail data
  trailGfx?: Phaser.GameObjects.Graphics;
  trail?: { x: number; y: number }[];
  smokeDist?: number;
  ownerId?: string;
  bounced?: boolean; // true after ricochet — prevents chain bounces
}

// ── Beam (instant hit, visual only lingers) ──
interface BeamEffect {
  line: Phaser.GameObjects.Graphics;
  timer: number; // seconds remaining
}

// ── Flame cone (area damage over time) ──
interface FlameEffect {
  gfx: Phaser.GameObjects.Graphics;
  sx: number; sy: number;
  angle: number;        // direction angle
  coneAngle: number;    // half-angle
  range: number;
  damage: number;       // damage per tick
  timer: number;        // seconds remaining
  tickTimer: number;    // time until next damage tick
  ownerId?: string;
}

// ── Acid blob (lobbed AoE + poison) ──
interface AcidBlob {
  visual: Phaser.GameObjects.Container;
  shadow: Phaser.GameObjects.Ellipse;
  sx: number; sy: number;       // start position
  tx: number; ty: number;       // target position
  damage: number;
  splashRadius: number;
  poisonDuration: number;
  poisonDps: number;
  progress: number;             // 0→1 interpolation
  duration: number;             // flight time in seconds
  arcHeight: number;            // parabola peak height (px)
  alive: boolean;
  ownerId?: string;
}

/**
 * Manages all enemy ranged attacks:
 * - CN/CL/R: traveling projectiles (circle-based visuals)
 * - LZ: instant-hit beam with brief visual
 * - LF: flame cone with area damage over time
 * - MA: arcing homing missile
 */
export class EnemyProjectileManager {
  private scene: Phaser.Scene;
  private projectiles: EnemyProjectile[] = [];
  private beams: BeamEffect[] = [];
  private flames: FlameEffect[] = [];
  private acidBlobs: AcidBlob[] = [];

  constructor(scene: Phaser.Scene) {
    this.scene = scene;
  }

  // ── Spawn methods ──

  /** Spawn a traveling projectile (CN, CL, R burst shot, MA missile) */
  spawnProjectile(
    sx: number, sy: number, tx: number, ty: number,
    damage: number, speed: number, color: number, range: number,
    weaponType: EnemyWeaponType,
    ownerId?: string,
  ): void {
    const dx = tx - sx;
    const dy = ty - sy;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist < 1) return;

    const vx = (dx / dist) * speed;
    const vy = (dy / dist) * speed;

    // Build projectile visual as a Container with circle children
    const visual = this.createProjectileVisual(weaponType, color);
    visual.setPosition(sx, sy);

    const isMissile = weaponType === EnemyWeaponType.MISSILE_AIR;
    const proj: EnemyProjectile = {
      visual, x: sx, y: sy, vx, vy,
      damage, color, speed,
      alive: true, maxDist: range * 1.5, traveled: 0,
      weaponType,
      // Trail: luminous polyline for bullets, smoke distance tracker for missiles
      trailGfx: isMissile ? undefined : this.scene.add.graphics().setDepth(19),
      trail: isMissile ? undefined : [],
      smokeDist: isMissile ? 0 : undefined,
      ownerId,
    };

    // Missiles get homing data
    if (isMissile) {
      proj.tx = tx; proj.ty = ty;
      proj.homingStrength = 3.0; // radians/s turn rate
    }

    this.projectiles.push(proj);
  }

  /** Spawn an instant-hit beam (LZ) — damages immediately, visual lingers */
  spawnBeam(
    sx: number, sy: number, tx: number, ty: number,
    damage: number, color: number,
    robots: RobotTarget[],
  ): void {
    // Instant damage to closest robot along beam line
    const dx = tx - sx;
    const dy = ty - sy;
    const len = Math.sqrt(dx * dx + dy * dy);
    if (len < 1) return;

    // Find robot closest to beam line within hit radius
    for (const robot of robots) {
      if (!robot.alive) continue;
      const rx = robot.x - sx;
      const ry = robot.y - sy;
      // Project robot onto beam direction
      const dot = (rx * dx + ry * dy) / (len * len);
      if (dot < 0 || dot > 1) continue;
      const closestX = sx + dx * dot;
      const closestY = sy + dy * dot;
      const distToLine = Math.sqrt((robot.x - closestX) ** 2 + (robot.y - closestY) ** 2);
      const hitR = robot.collisionRadius ?? 20;
      if (distToLine <= hitR) {
        // ── Beam ricochet check (level 3 armor) ──
        if (robot.checkRicochet) {
          const ric = robot.checkRicochet(EnemyWeaponType.LASER, closestX, closestY);
          if (ric) {
            // Reflected beam visual (VFX only, no damage to enemies)
            const reflLen = len * 0.6; // shorter reflected beam
            const reflEndX = ric.impactX + ric.normalX * reflLen;
            const reflEndY = ric.impactY + ric.normalY * reflLen;
            tx = ric.impactX; // shorten original beam to impact point
            ty = ric.impactY;
            // Draw reflected beam after main beam (below)
            const rGfx = this.scene.add.graphics().setDepth(20);
            rGfx.lineStyle(3, 0x4488ff, 0.9);
            rGfx.beginPath(); rGfx.moveTo(ric.impactX, ric.impactY);
            rGfx.lineTo(reflEndX, reflEndY); rGfx.strokePath();
            rGfx.lineStyle(8, 0x4488ff, 0.15);
            rGfx.beginPath(); rGfx.moveTo(ric.impactX, ric.impactY);
            rGfx.lineTo(reflEndX, reflEndY); rGfx.strokePath();
            this.beams.push({ line: rGfx, timer: 0.15 });
            this.spawnRicochetSpark(ric.impactX, ric.impactY, color);
            break; // skip damage
          }
        }
        robot.takeDamage(damage, closestX, closestY);
        break; // beam hits first robot only
      }
    }

    // Visual: bright beam line that fades
    const gfx = this.scene.add.graphics().setDepth(20);
    // Core beam
    gfx.lineStyle(3, color, 0.9);
    gfx.beginPath();
    gfx.moveTo(sx, sy);
    gfx.lineTo(tx, ty);
    gfx.strokePath();
    // Glow
    gfx.lineStyle(8, color, 0.2);
    gfx.beginPath();
    gfx.moveTo(sx, sy);
    gfx.lineTo(tx, ty);
    gfx.strokePath();

    // Impact spark at target
    const spark = this.scene.add.circle(tx, ty, 6, color, 0.8).setDepth(21);
    this.scene.tweens.add({
      targets: spark, alpha: 0, scaleX: 2.5, scaleY: 2.5,
      duration: 200, onComplete: () => spark.destroy(),
    });

    this.beams.push({ line: gfx, timer: 0.15 });
  }

  /** Spawn a flame cone (LF) — area damage over duration */
  spawnFlame(
    sx: number, sy: number, tx: number, ty: number,
    damage: number, range: number, color: number,
    coneAngle: number, duration: number,
    ownerId?: string,
  ): void {
    const angle = Math.atan2(ty - sy, tx - sx);
    const gfx = this.scene.add.graphics().setDepth(20);
    this.drawFlameCone(gfx, sx, sy, angle, coneAngle, range, color);

    this.flames.push({
      gfx, sx, sy, angle, coneAngle, range,
      damage,
      timer: duration,
      tickTimer: 0,
      ownerId,
    });
  }

  /** Spawn a lobbed acid blob (AC) — parabolic arc, AoE splash + poison on impact */
  spawnAcidBlob(
    sx: number, sy: number, tx: number, ty: number,
    damage: number, splashRadius: number,
    poisonDuration: number, poisonDps: number,
    ownerId?: string,
  ): void {
    const dist = Math.sqrt((tx - sx) ** 2 + (ty - sy) ** 2);
    const arcHeight = Math.max(50, dist * 0.6);   // high arc for lobbed catapult feel
    const duration = Math.max(0.6, dist / 60);    // slow lobbed trajectory

    // Visual: animated acid sprite rotated toward target
    const angle = Math.atan2(ty - sy, tx - sx);
    const container = this.scene.add.container(sx, sy).setDepth(24);
    const acidSprite = this.scene.add.sprite(0, 0, 'acid')
      .setScale(0.12)
      .setRotation(angle)
      .play('acid_fly');
    container.add(acidSprite);
    // Outer glow
    container.add(this.scene.add.circle(0, 0, 10, 0x66ff22, 0.15));

    // Shadow on ground (follows XY linearly)
    const shadow = this.scene.add.ellipse(sx, sy, 8, 4, 0x000000, 0.3).setDepth(5);

    this.acidBlobs.push({
      visual: container, shadow,
      sx, sy, tx, ty,
      damage, splashRadius, poisonDuration, poisonDps,
      progress: 0, duration, arcHeight,
      alive: true, ownerId,
    });
  }

  // ── Update ──

  update(delta: number, robots: RobotTarget[], robotHash?: SpatialHash<RobotTarget>, enemies?: EnemyTarget[]): void {
    const dt = delta / 1000;
    this.updateProjectiles(dt, robots, robotHash, enemies);
    this.updateBeams(dt);
    this.updateFlames(dt, robots);
    this.updateAcidBlobs(dt, robots);
  }

  private updateProjectiles(dt: number, robots: RobotTarget[], robotHash?: SpatialHash<RobotTarget>, enemies?: EnemyTarget[]): void {
    for (const p of this.projectiles) {
      if (!p.alive) continue;

      const prevX = p.x;
      const prevY = p.y;

      // Missile homing: adjust velocity toward last known target
      if (p.weaponType === EnemyWeaponType.MISSILE_AIR && p.tx !== undefined && p.ty !== undefined) {
        const desiredDx = p.tx - p.x;
        const desiredDy = p.ty - p.y;
        const desiredAngle = Math.atan2(desiredDy, desiredDx);
        const currentAngle = Math.atan2(p.vy, p.vx);
        let angleDiff = desiredAngle - currentAngle;
        while (angleDiff > Math.PI) angleDiff -= Math.PI * 2;
        while (angleDiff < -Math.PI) angleDiff += Math.PI * 2;
        const turn = Math.sign(angleDiff) * Math.min(Math.abs(angleDiff), (p.homingStrength ?? 3) * dt);
        const newAngle = currentAngle + turn;
        p.vx = Math.cos(newAngle) * p.speed;
        p.vy = Math.sin(newAngle) * p.speed;
      }

      const stepX = p.vx * dt;
      const stepY = p.vy * dt;
      p.x += stepX;
      p.y += stepY;
      p.traveled += Math.sqrt(stepX * stepX + stepY * stepY);
      p.visual.setPosition(p.x, p.y);

      // Rotate missile visual to face direction
      if (p.weaponType === EnemyWeaponType.MISSILE_AIR) {
        p.visual.setRotation(Math.atan2(p.vy, p.vx));
      }

      // ── Trail rendering ──
      if (p.weaponType === EnemyWeaponType.MISSILE_AIR) {
        this.updateSmokeTrail(p, prevX, prevY);
      } else if (p.trail && p.trailGfx) {
        this.updateLuminousTrail(p);
      }

      // Wall collision: non-missile projectiles are destroyed on wall tiles
      if (p.weaponType !== EnemyWeaponType.MISSILE_AIR) {
        const col = Math.floor(p.x / TILE_SIZE);
        const row = Math.floor(p.y / TILE_SIZE);
        if (MAP_GRID[row]?.[col] === TileType.WALL) {
          p.alive = false;
          p.visual.destroy();
          p.trailGfx?.destroy();
          continue;
        }
      }

      if (p.traveled >= p.maxDist) {
        p.alive = false;
        p.visual.destroy();
        p.trailGfx?.destroy();
        continue;
      }

      // Bounced projectiles collide with enemies instead of robots
      if (p.bounced && enemies) {
        for (const enemy of enemies) {
          if (!enemy.alive) continue;
          const ex = p.x - enemy.x;
          const ey = p.y - enemy.y;
          const hitR = enemy.collisionRadius ?? 16;
          if (ex * ex + ey * ey <= hitR * hitR) {
            enemy.takeDamage(p.damage);
            p.alive = false;
            p.visual.destroy();
            p.trailGfx?.destroy();
            this.spawnImpact(p.x, p.y, p.color, p.weaponType);
            break;
          }
        }
        continue;
      }

      // Collision with robots — use spatial hash if available, else fallback
      const candidates = robotHash
        ? robotHash.queryRadius(p.x, p.y, 30)
        : robots;
      for (const robot of candidates) {
        if (!robot.alive) continue;
        const dx = p.x - robot.x;
        const dy = p.y - robot.y;
        const hitR = robot.collisionRadius ?? 20;
        if (dx * dx + dy * dy <= hitR * hitR) {
          // ── Ricochet check (before applying damage) ──
          if (!p.bounced && robot.checkRicochet) {
            const ric = robot.checkRicochet(p.weaponType, p.x, p.y);
            if (ric) {
              // Reflect velocity: v' = v - 2(v·n)n
              const dot = p.vx * ric.normalX + p.vy * ric.normalY;
              let rvx = p.vx - 2 * dot * ric.normalX;
              let rvy = p.vy - 2 * dot * ric.normalY;
              // Add ±10° random spread
              const spread = (Math.random() - 0.5) * (Math.PI / 9);
              const cos = Math.cos(spread);
              const sin = Math.sin(spread);
              p.vx = rvx * cos - rvy * sin;
              p.vy = rvx * sin + rvy * cos;
              p.bounced = true;
              p.traveled = 0;
              p.x = ric.impactX;
              p.y = ric.impactY;
              p.visual.setPosition(p.x, p.y);
              this.spawnRicochetSpark(ric.impactX, ric.impactY, p.color);
              break; // skip damage, projectile continues
            }
          }
          robot.takeDamage(p.damage, p.x, p.y);
          p.alive = false;
          p.visual.destroy();
          p.trailGfx?.destroy();
          this.spawnImpact(p.x, p.y, p.color, p.weaponType);
          break;
        }
      }
    }
    this.projectiles = this.projectiles.filter(p => p.alive);
  }

  private updateBeams(dt: number): void {
    for (let i = this.beams.length - 1; i >= 0; i--) {
      const b = this.beams[i];
      b.timer -= dt;
      if (b.timer <= 0) {
        b.line.destroy();
        this.beams.splice(i, 1);
      } else {
        b.line.setAlpha(b.timer / 0.15);
      }
    }
  }

  private updateFlames(dt: number, robots: RobotTarget[]): void {
    const FLAME_TICK = 0.15; // damage tick interval (seconds)

    for (let i = this.flames.length - 1; i >= 0; i--) {
      const f = this.flames[i];
      f.timer -= dt;
      if (f.timer <= 0) {
        f.gfx.destroy();
        this.flames.splice(i, 1);
        continue;
      }

      // Fade flame visual
      f.gfx.setAlpha(Math.min(1, f.timer * 3));

      // Damage tick
      f.tickTimer -= dt;
      if (f.tickTimer <= 0) {
        f.tickTimer = FLAME_TICK;
        // Damage all robots inside the cone
        for (const robot of robots) {
          if (!robot.alive) continue;
          if (this.isInCone(robot.x, robot.y, f.sx, f.sy, f.angle, f.coneAngle, f.range)) {
            robot.takeDamage(f.damage, f.sx, f.sy);
          }
        }
      }
    }
  }

  private updateAcidBlobs(dt: number, robots: RobotTarget[]): void {
    for (let i = this.acidBlobs.length - 1; i >= 0; i--) {
      const b = this.acidBlobs[i];
      if (!b.alive) { this.acidBlobs.splice(i, 1); continue; }

      b.progress += dt / b.duration;

      if (b.progress >= 1) {
        // Impact — AoE splash + poison
        b.alive = false;
        b.visual.destroy();
        b.shadow.destroy();
        this.spawnAcidSplash(b.tx, b.ty, b.splashRadius, b.damage, b.poisonDuration, b.poisonDps, robots);
        this.acidBlobs.splice(i, 1);
        continue;
      }

      // Parabolic arc: Y offset = -arcHeight * sin(progress * PI)
      const t = b.progress;
      const groundX = b.sx + (b.tx - b.sx) * t;
      const groundY = b.sy + (b.ty - b.sy) * t;
      const altitude = -b.arcHeight * Math.sin(t * Math.PI);

      const prevVisX = b.visual.x;
      const prevVisY = b.visual.y;
      b.visual.setPosition(groundX, groundY + altitude);
      b.shadow.setPosition(groundX, groundY);

      // Rotate sprite to follow trajectory tangent
      const mdx = b.visual.x - prevVisX;
      const mdy = b.visual.y - prevVisY;
      if (mdx !== 0 || mdy !== 0) {
        b.visual.setRotation(Math.atan2(mdy, mdx));
      }

      // Scale shadow based on altitude (smaller when higher)
      const shadowScale = 1 - Math.abs(altitude) / b.arcHeight * 0.5;
      b.shadow.setScale(shadowScale, shadowScale * 0.5);

      // Drip trail particles
      if (Math.random() < 0.3) {
        const drip = this.scene.add.circle(
          groundX + (Math.random() - 0.5) * 4,
          groundY + altitude + (Math.random() - 0.5) * 4,
          2, 0x66ff22, 0.6,
        ).setDepth(23);
        this.scene.tweens.add({
          targets: drip, alpha: 0, y: drip.y + 10, scaleX: 0.3, scaleY: 0.3,
          duration: 400 + Math.random() * 200, onComplete: () => drip.destroy(),
        });
      }
    }
  }

  /** AoE splash on acid impact — damages + applies poison to nearby robots */
  private spawnAcidSplash(
    x: number, y: number, radius: number,
    damage: number, poisonDuration: number, poisonDps: number,
    robots: RobotTarget[],
  ): void {
    // Visual: expanding green ring + puddle
    const ring = this.scene.add.circle(x, y, radius * 0.3, 0x66ff22, 0.6).setDepth(22);
    this.scene.tweens.add({
      targets: ring, alpha: 0, scaleX: 3, scaleY: 3,
      duration: 400, onComplete: () => ring.destroy(),
    });
    // Acid puddle (lingers briefly)
    const puddle = this.scene.add.ellipse(x, y, radius * 1.5, radius * 0.8, 0x44aa11, 0.35).setDepth(5);
    this.scene.tweens.add({
      targets: puddle, alpha: 0, duration: 1500, delay: 300,
      onComplete: () => puddle.destroy(),
    });
    // Splash particles
    for (let p = 0; p < 6; p++) {
      const angle = Math.random() * Math.PI * 2;
      const dist = Math.random() * radius * 0.6;
      const px = x + Math.cos(angle) * dist;
      const py = y + Math.sin(angle) * dist;
      const droplet = this.scene.add.circle(px, py, 2 + Math.random() * 2, 0x88ff33, 0.8).setDepth(23);
      this.scene.tweens.add({
        targets: droplet, alpha: 0,
        x: px + Math.cos(angle) * 15, y: py + Math.sin(angle) * 15,
        duration: 300 + Math.random() * 200, onComplete: () => droplet.destroy(),
      });
    }

    // Damage + poison all robots in splash radius
    const splashR = GOD.hiveAcidSplashRadius;
    for (const robot of robots) {
      if (!robot.alive) continue;
      const dx = robot.x - x;
      const dy = robot.y - y;
      if (dx * dx + dy * dy <= splashR * splashR) {
        robot.takeDamage(damage, x, y);
        // Apply poison via the poison interface (if available)
        if ((robot as any).applyPoison) {
          (robot as any).applyPoison(poisonDuration, poisonDps);
        }
      }
    }
  }

  // ── Helpers ──

  private isInCone(
    px: number, py: number,
    cx: number, cy: number,
    angle: number, halfAngle: number, range: number,
  ): boolean {
    const dx = px - cx;
    const dy = py - cy;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist > range) return false;
    const angleToP = Math.atan2(dy, dx);
    let diff = angleToP - angle;
    while (diff > Math.PI) diff -= Math.PI * 2;
    while (diff < -Math.PI) diff += Math.PI * 2;
    return Math.abs(diff) <= halfAngle;
  }

  /** Luminous trail: fading polyline behind bullet/shell projectiles */
  private updateLuminousTrail(p: EnemyProjectile): void {
    const trail = p.trail!;
    const gfx = p.trailGfx!;
    trail.push({ x: p.x, y: p.y });
    if (trail.length > TRAIL_MAX) trail.shift();
    gfx.clear();
    if (trail.length < 2) return;
    const isHeavy = p.weaponType === EnemyWeaponType.CANNON_HEAVY;
    const lineW = isHeavy ? 3.5 : 2;
    for (let i = 1; i < trail.length; i++) {
      const t = i / trail.length;
      // Core trail line
      gfx.lineStyle(lineW * t, p.color, t * 0.8);
      gfx.beginPath();
      gfx.moveTo(trail[i - 1].x, trail[i - 1].y);
      gfx.lineTo(trail[i].x, trail[i].y);
      gfx.strokePath();
      // Wider dim glow around the trail
      gfx.lineStyle(lineW * t * 3, p.color, t * 0.12);
      gfx.beginPath();
      gfx.moveTo(trail[i - 1].x, trail[i - 1].y);
      gfx.lineTo(trail[i].x, trail[i].y);
      gfx.strokePath();
    }
  }

  /** Smoke trail: spawn fx_smoke images along missile path that evaporate */
  private updateSmokeTrail(p: EnemyProjectile, prevX: number, prevY: number): void {
    const dx = p.x - prevX;
    const dy = p.y - prevY;
    p.smokeDist = (p.smokeDist ?? 0) + Math.sqrt(dx * dx + dy * dy);
    while (p.smokeDist! >= SMOKE_INTERVAL) {
      p.smokeDist! -= SMOKE_INTERVAL;
      const smoke = this.scene.add.image(
        prevX + (Math.random() - 0.5) * 4,
        prevY + (Math.random() - 0.5) * 4,
        'fx_smoke',
      );
      smoke.setDepth(18);
      smoke.setScale(0.3 + Math.random() * 0.2);
      smoke.setAlpha(0.6);
      smoke.setTint(0xff8844);
      this.scene.tweens.add({
        targets: smoke,
        alpha: 0,
        scaleX: smoke.scaleX * 2.5,
        scaleY: smoke.scaleY * 2.5,
        y: smoke.y - 8 - Math.random() * 8,
        duration: 2500 + Math.random() * 1200,
        onComplete: () => smoke.destroy(),
      });
    }
  }

  /**
   * Create projectile visual as a Container with Circle children.
   * Uses scene.add.circle() which renders reliably in WebGL
   * (unlike Graphics.fillPoints which silently fails).
   */
  private createProjectileVisual(type: EnemyWeaponType, color: number): Phaser.GameObjects.Container {
    const container = this.scene.add.container(0, 0).setDepth(22);

    switch (type) {
      case EnemyWeaponType.CANNON_HEAVY: {
        // Outer glow
        container.add(this.scene.add.circle(0, 0, 8, color, 0.2));
        // Core
        container.add(this.scene.add.circle(0, 0, 4.5, color, 0.95));
        // Bright center
        container.add(this.scene.add.circle(0, 0, 2, 0xffffff, 0.5));
        break;
      }

      case EnemyWeaponType.MISSILE_AIR: {
        // Outer glow
        container.add(this.scene.add.circle(0, 0, 12, color, 0.15));
        // Exhaust glow (offset behind)
        container.add(this.scene.add.circle(-6, 0, 5, 0xff6600, 0.5));
        // Core (elongated approximation: wider circle)
        container.add(this.scene.add.circle(2, 0, 6, color, 0.95));
        // Bright tip
        container.add(this.scene.add.circle(6, 0, 3, 0xffffff, 0.6));
        break;
      }

      case EnemyWeaponType.BURST: {
        // Outer glow
        container.add(this.scene.add.circle(0, 0, 5, color, 0.2));
        // Core
        container.add(this.scene.add.circle(0, 0, 2.5, color, 0.95));
        // Bright center
        container.add(this.scene.add.circle(0, 0, 1, 0xffffff, 0.6));
        break;
      }

      default: { // CANNON_NORMAL (Grunt, etc.)
        // Outer glow
        container.add(this.scene.add.circle(0, 0, 4, color, 0.2));
        // Core
        container.add(this.scene.add.circle(0, 0, 2, color, 0.95));
        // Bright center
        container.add(this.scene.add.circle(0, 0, 1, 0xffffff, 0.7));
        break;
      }
    }

    return container;
  }

  /** Draw flame cone shape */
  private drawFlameCone(
    gfx: Phaser.GameObjects.Graphics,
    cx: number, cy: number,
    angle: number, halfAngle: number, range: number, color: number,
  ): void {
    gfx.fillStyle(color, 0.4);
    gfx.beginPath();
    gfx.moveTo(cx, cy);
    const steps = 12;
    for (let i = 0; i <= steps; i++) {
      const a = angle - halfAngle + (halfAngle * 2) * (i / steps);
      // Jagged flame edge
      const r = range * (0.85 + Math.random() * 0.15);
      gfx.lineTo(cx + Math.cos(a) * r, cy + Math.sin(a) * r);
    }
    gfx.closePath();
    gfx.fillPath();
    // Inner brighter core
    gfx.fillStyle(0xffff44, 0.25);
    gfx.beginPath();
    gfx.moveTo(cx, cy);
    for (let i = 0; i <= steps; i++) {
      const a = angle - halfAngle * 0.6 + (halfAngle * 1.2) * (i / steps);
      gfx.lineTo(cx + Math.cos(a) * range * 0.5, cy + Math.sin(a) * range * 0.5);
    }
    gfx.closePath();
    gfx.fillPath();
  }

  /** Blue spark VFX when a projectile ricochets off armor */
  private spawnRicochetSpark(x: number, y: number, color: number): void {
    // Blue core flash
    const core = this.scene.add.circle(x, y, 5, 0x4488ff, 1).setDepth(24);
    this.scene.tweens.add({
      targets: core, alpha: 0, scaleX: 3, scaleY: 3,
      duration: 150, onComplete: () => core.destroy(),
    });
    // Colored outer ring
    const ring = this.scene.add.circle(x, y, 8, color, 0.5).setDepth(23);
    this.scene.tweens.add({
      targets: ring, alpha: 0, scaleX: 2, scaleY: 2,
      duration: 200, onComplete: () => ring.destroy(),
    });
  }

  /** Impact effect on projectile hit */
  private spawnImpact(x: number, y: number, color: number, type: EnemyWeaponType): void {
    const radius = type === EnemyWeaponType.CANNON_HEAVY ? 16 :
                   type === EnemyWeaponType.MISSILE_AIR ? 18 : 10;
    // Bright core flash
    const core = this.scene.add.circle(x, y, radius * 0.4, 0xffffff, 0.9).setDepth(23);
    this.scene.tweens.add({
      targets: core, alpha: 0, scaleX: 3, scaleY: 3,
      duration: 120,
      onComplete: () => core.destroy(),
    });
    // Colored expanding ring
    const flash = this.scene.add.circle(x, y, radius, color, 0.8).setDepth(22);
    this.scene.tweens.add({
      targets: flash, alpha: 0, scaleX: 2.5, scaleY: 2.5,
      duration: type === EnemyWeaponType.MISSILE_AIR ? 350 : 220,
      onComplete: () => flash.destroy(),
    });
  }

  /** Remove all projectiles & flames belonging to a specific enemy (on death). */
  removeByOwner(ownerId: string): void {
    for (const p of this.projectiles) {
      if (p.alive && p.ownerId === ownerId) {
        p.alive = false;
        p.visual?.destroy();
        p.trailGfx?.destroy();
      }
    }
    for (const f of this.flames) {
      if (f.ownerId === ownerId && f.timer > 0) {
        f.timer = 0;
        f.gfx?.destroy();
      }
    }
    for (const a of this.acidBlobs) {
      if (a.alive && a.ownerId === ownerId) {
        a.alive = false;
        a.visual?.destroy();
        a.shadow?.destroy();
      }
    }
  }

  /** Remove all effects (scene shutdown). */
  clear(): void {
    for (const p of this.projectiles) {
      if (p.visual) p.visual.destroy();
      if (p.trailGfx) p.trailGfx.destroy();
    }
    for (const b of this.beams) { if (b.line) b.line.destroy(); }
    for (const f of this.flames) { if (f.gfx) f.gfx.destroy(); }
    for (const a of this.acidBlobs) { a.visual?.destroy(); a.shadow?.destroy(); }
    this.projectiles = [];
    this.beams = [];
    this.flames = [];
    this.acidBlobs = [];
  }
}
